import { type KeyboardEvent, type ReactNode, type RefObject, useEffect, useRef } from "react";
import { CheckIcon, CloseIcon } from "./icons.tsx";

/*
 * Bulk selection's controls (docs/2026-09-30-inbox-bulk-selection-shape.md): the row checkbox and the
 * bar that takes the heading's place while rows are ticked. Quiet at rest: nothing here is drawn
 * until the rep reaches for a row's checkbox, presses X, Shift+J/K or Cmd/Ctrl+A in the list, or
 * starts selecting on a phone.
 */

/*
 * A row's checkbox. A native checkbox, visually hidden inside a label that draws the box, so it is
 * focusable, announced and toggled by Space like any checkbox. It is a sibling of the row's own
 * button, because a control nested in a button is neither announced nor reachable. Shift is read on
 * the press, since a click relayed from the label to its input may not carry it; Space with Shift
 * held ranges too. Where it shows, it takes the unseen dot's place (DESIGN.md, List rows).
 */
export function CheckBox(props: {
  checked: boolean | "mixed";
  label: string;
  className?: string;
  tabbable: boolean;
  /** Shown but not changeable (a mailbox already in the group being filled, or not linked yet). */
  disabled?: boolean;
  onFocus?: (() => void) | undefined;
  onToggle: (shiftKey: boolean) => void;
}) {
  const shift = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (input.current) input.current.indeterminate = props.checked === "mixed";
  }, [props.checked]);
  return (
    <label
      className={`lcr-check ${props.className ?? ""}`}
      onMouseDown={(event) => {
        shift.current = event.shiftKey;
        // Shift+click would otherwise select the text between here and the last click.
        if (event.shiftKey) event.preventDefault();
      }}
      onKeyDown={(event) => {
        if (event.key === " ") shift.current = event.shiftKey;
      }}
    >
      <input
        ref={input}
        type="checkbox"
        className="lcr-check-input"
        checked={props.checked === true}
        aria-label={props.label}
        disabled={props.disabled === true}
        tabIndex={props.tabbable ? 0 : -1}
        onFocus={props.onFocus}
        onChange={() => {
          const shiftKey = shift.current;
          shift.current = false;
          props.onToggle(shiftKey);
        }}
      />
      <span className="lcr-check-box" aria-hidden="true">
        {props.checked === true && <CheckIcon size={12} />}
        {props.checked === "mixed" && <span className="lcr-check-dash" />}
      </span>
    </label>
  );
}

/**
 * A toolbar's arrows (WAI-ARIA toolbar pattern): Left and Right move along its controls, wrapping,
 * and leave an open menu's own keys alone.
 */
function stepAlong(event: KeyboardEvent<HTMLDivElement>): void {
  const step = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
  const at = event.target;
  if (step === 0 || !(at instanceof HTMLElement) || at.closest('[role="menu"], [role="dialog"]'))
    return;
  const stops = [
    ...event.currentTarget.querySelectorAll<HTMLElement>("button:enabled, input:enabled"),
  ].filter((stop) => !stop.closest('[role="menu"], [role="dialog"]'));
  const index = stops.indexOf(at);
  if (index === -1) return;
  event.preventDefault();
  stops[(index + step + stops.length) % stops.length]?.focus();
}

/**
 * The bar that takes the list heading's place while rows are ticked: the master inbox's (step 10,
 * C5b left it one caller, `SelectionBar` in `inbox-v2/list-column.tsx`, which chooses its actions
 * and how far they fold). Its count is drawn for the eye; the list says it aloud (`SelectionStatus`),
 * so it is hidden from screen readers here. The count never shrinks and × is always the bar's last
 * control: the actions give way to them, never the other way round.
 */
export function BulkBar(props: {
  /** The bar itself, for the caller's measured fold: × is its last child, so it is what overflows. */
  bar: RefObject<HTMLDivElement | null>;
  header: "none" | "some" | "all";
  /** The count, in words or as its number alone (the narrowest bar). */
  count: string;
  onToggleAll: () => void;
  onClear: () => void;
  /** The actions, drawn as `.lcr-bulk-action` buttons, between the count and ×. */
  children: ReactNode;
}) {
  return (
    <div
      ref={props.bar}
      className="lcr-bulk-bar"
      role="toolbar"
      aria-label="Selected conversations"
      onKeyDown={stepAlong}
    >
      <CheckBox
        className="lcr-bulk-all"
        checked={props.header === "all" ? true : props.header === "some" ? "mixed" : false}
        label={
          props.header === "all"
            ? "Clear the selection of every conversation shown"
            : "Select every conversation shown"
        }
        tabbable
        onToggle={props.onToggleAll}
      />
      <span className="lcr-bulk-count" aria-hidden="true">
        {props.count}
      </span>
      <span className="lcr-bulk-actions">{props.children}</span>
      <button
        type="button"
        className="lcr-bulk-clear"
        aria-label="Clear the selection"
        aria-keyshortcuts="Escape"
        title="Clear the selection (Esc)"
        onClick={props.onClear}
      >
        <CloseIcon size={16} />
      </button>
    </div>
  );
}
